import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Chip, CircularProgress, Grid, LinearProgress, MenuItem, Paper, TablePagination, TextField, Typography,
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import { formatDistanceToNow } from "date-fns";
import { toast } from "react-toastify";
import api from "../../api";
import { CARD_SX } from "./AnalyticsPageShell";
import AnalyticsDataTable from "./AnalyticsDataTable";

/**
 * App Engagement → "App users" (12:02 meeting: why do users stop using the app).
 * Top: of the people who signed up in the chosen dates, how many used the app on day 1, came
 * back by day 3 and were still using it on day 7. Below: who has stopped opening it, and what
 * they did last. API: GET admin/analytics/app-usage/app-users.
 */
const PLAN_CHIP = {
  trial: { label: "Free trial", bg: "#e0e7ff", color: "#4338ca" },
  paid: { label: "Paid", bg: "#dcfce7", color: "#15803d" },
  expired: { label: "Expired", bg: "#fee2e2", color: "#b91c1c" },
  none: { label: "No plan", bg: "#f1f5f9", color: "#475569" },
};

const INACTIVE_OPTIONS = [3, 7, 14, 30];

const relative = (iso) => {
  try {
    return formatDistanceToNow(new Date(iso.replace(" ", "T")), { addSuffix: true });
  } catch {
    return iso;
  }
};

const percent = (count, eligible) => (eligible > 0 ? Math.round((count * 100) / eligible) : null);

function CohortStep({ label, count, eligible, note }) {
  const pct = percent(count, eligible);
  return (
    <Paper sx={{ ...CARD_SX, height: "100%" }}>
      <Typography variant="body2" sx={{ fontWeight: 700, color: "#4b5563" }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 800, my: 0.5 }}>{count.toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</Typography>
      <LinearProgress
        variant="determinate"
        value={pct ?? 0}
        aria-label={`${label}: ${pct ?? 0} percent`}
        sx={{ height: 8, borderRadius: 999, bgcolor: "#ede9fe", "& .MuiLinearProgress-bar": { bgcolor: "#5b21b6" } }}
      />
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
        {note || (pct === null ? "Too early to tell" : `${pct}% of the ${eligible} who could have reached this`)}
      </Typography>
    </Paper>
  );
}

export default function AppUsersEngagement() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [inactiveDays, setInactiveDays] = useState(3);
  const [plan, setPlan] = useState("");
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(20);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Typing is applied after a pause, not on every key.
  useEffect(() => {
    const timer = setTimeout(() => {
      setAppliedSearch(search.trim());
      setPage(0);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("admin/analytics/app-usage/app-users", {
        params: {
          from: from || undefined,
          to: to || undefined,
          inactive_days: inactiveDays,
          plan: plan || undefined,
          search: appliedSearch || undefined,
          page: page + 1,
          per_page: perPage,
        },
      });
      setData(res.data?.data || null);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load app users");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, inactiveDays, plan, appliedSearch, page, perPage]);

  useEffect(() => { load(); }, [load]);

  const change = (setter) => (event) => {
    setter(event.target.value);
    setPage(0);
  };

  const cohort = data?.cohort;
  const stopped = data?.stopped;

  const columns = [
    {
      key: "name",
      label: "User",
      render: (r) => (
        <Box>
          <Box component={RouterLink} to={`/admin/mobile-users/${r.id}/progress`} sx={{ fontWeight: 700, color: "#5b21b6" }}>
            {r.name}
          </Box>
          <Typography variant="caption" color="text.secondary" display="block">{r.email}</Typography>
        </Box>
      ),
    },
    {
      key: "plan",
      label: "Plan",
      render: (r) => {
        const chip = PLAN_CHIP[r.plan] || PLAN_CHIP.none;
        return <Chip size="small" label={chip.label} sx={{ bgcolor: chip.bg, color: chip.color, fontWeight: 600 }} />;
      },
    },
    { key: "joined", label: "Joined", render: (r) => <span style={{ whiteSpace: "nowrap" }}>{r.joined}</span> },
    {
      key: "last_active",
      label: "Last active",
      render: (r) => (r.never_opened ? "Never opened" : relative(r.last_active)),
    },
    { key: "days_inactive", label: "Days inactive", render: (r) => <strong>{r.days_inactive}</strong> },
    { key: "visits", label: "App visits" },
    {
      key: "done",
      label: "Done (worksheets · mocks · games · cards)",
      render: (r) => [r.done.worksheets, r.done.mocks, r.done.games, r.done.cards].join(" · "),
    },
    {
      key: "last_thing",
      label: "Last thing they did",
      render: (r) => (r.last_thing ? `${r.last_thing.what}, ${relative(r.last_thing.at)}` : "Nothing yet"),
    },
  ];

  return (
    <>
      <Paper sx={{ ...CARD_SX, mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", flexWrap: "wrap", gap: 1, mb: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Who keeps going after sign-up</Typography>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <TextField
              size="small" type="date" label="Signed up from" value={from} onChange={change(setFrom)}
              InputLabelProps={{ shrink: true }} sx={{ width: 170 }}
            />
            <TextField
              size="small" type="date" label="Signed up to" value={to} onChange={change(setTo)}
              InputLabelProps={{ shrink: true }} sx={{ width: 170 }}
            />
          </Box>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1.5 }}>
          {data?.window ? `Sign-ups from ${data.window.from} to ${data.window.to}. ` : ""}
          Each step only counts people who have been signed up long enough to reach it.
        </Typography>
        {loading && !cohort ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}><CircularProgress size={28} /></Box>
        ) : cohort ? (
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6} md={3}>
              <CohortStep label="Signed up" count={cohort.signed_up} eligible={cohort.signed_up} note="Everyone in these dates" />
            </Grid>
            {cohort.steps.map((step) => (
              <Grid item xs={12} sm={6} md={3} key={step.key}>
                <CohortStep label={step.label} count={step.count} eligible={step.eligible} />
              </Grid>
            ))}
          </Grid>
        ) : null}
      </Paper>

      <Paper sx={{ ...CARD_SX, mb: 2 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 1.5, mb: 1.5 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Stopped using the app</Typography>
            <Typography variant="body2" color="text.secondary">
              No app visit for {inactiveDays} days or more. Click a name for their progress. Test accounts are left out.
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
            <TextField select size="small" label="Inactive for" value={inactiveDays} onChange={change(setInactiveDays)} sx={{ width: 140 }}>
              {INACTIVE_OPTIONS.map((d) => <MenuItem key={d} value={d}>{d}+ days</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Plan" value={plan} onChange={change(setPlan)} sx={{ width: 150 }}>
              <MenuItem value="">All plans</MenuItem>
              <MenuItem value="trial">Free trial</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="none">No plan</MenuItem>
            </TextField>
            <TextField
              size="small" label="Name or email" value={search} onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 220 }}
            />
          </Box>
        </Box>

        <AnalyticsDataTable
          columns={columns}
          rows={stopped?.rows || []}
          loading={loading && !stopped}
          emptyMessage="No one has been away that long with these filters."
        />
        {stopped ? (
          <TablePagination
            component="div"
            count={stopped.total}
            page={page}
            rowsPerPage={perPage}
            rowsPerPageOptions={[10, 20, 50, 100]}
            onPageChange={(_, next) => setPage(next)}
            onRowsPerPageChange={(e) => { setPerPage(Number(e.target.value)); setPage(0); }}
          />
        ) : null}
      </Paper>
    </>
  );
}
